'use client';

import React, { useMemo } from 'react';

import dynamic from 'next/dynamic';

import Typography from '@components/Typography';
import { Row, Col, FlexibleDiv, Separator } from '@components/formStyles';
import { convertDateAsOnlyMonthString } from '@utils/dateUtil';
import { useDevice } from '@contexts/device';
import { SocialShare } from '@components/ui/social-share';
import GuestSupportBanner from '@components/ui/GuestsSuggestBanner';
import OpportunityCard from '@components/cards/opportunity/OpportunityCard';
import LoyaltyLoginBanner from '@components/ui/LoyaltyLoginBanner/LoyaltyLoginBanner';
import { haveLoyaltyPromotion } from '@components/views/travel-planning/utils';
import MyFavoriteComponent from '@components/views/my-favorites/MyFavoriteComponent';
import { FavoriteItemType } from '@api/v3';
import {
  useGetFavoriteListItems,
  useGetFavoriteLists,
} from '@components/views/my-favorites/hooks';
import { findDefaultList } from '@components/views/my-favorites/MyFavoriteComponent/utils';
import useGetasPath from '@hooks/useGetasPath';
import useToken from '@utils/useToken';

import { SummaryCardProps } from './props';
import {
  Title,
  TitleBorder,
  Wrapper,
  SpacingRow,
  Location,
  ReadMore,
  Date,
  ReadMoreEmptySection,
  DateDiv,
  TourCardContainer,
  StyledButton,
  IconContainer,
  DetailSegments,
} from './styled';
import { formatTourPromotions } from '../../detail/utils/formats';
import Segments from '../../components/next/Segments';

const SummaryCardMobileDescription = dynamic(
  () => import('./SummaryCardMobileDescription')
);
const SummaryCardDesktopDescription = dynamic(
  () => import('./SummaryCardDesktopDescription')
);

interface ReadMoreSectionProps {
  text: string;
  onClick?: () => void;
}
export const ReadMoreSection = ({ text, onClick }: ReadMoreSectionProps) => (
  <ReadMore onClick={onClick}>{text}</ReadMore>
);

const SummaryCad = ({
  onNavigate,
  onReadMore,
  name,
  description,
  prices,
  roomInformation,
  isExpedia = false,
  whereToGo,
  date,
  tourType,
  installments,
  isEmpty,
  toggleLetUsCallYou,
  isBadge,
  showKocAilem,
  toggleInstallments,
  promotions,
  itemId,
  imageUrl,
  segments,
}: SummaryCardProps) => {
  const asPath = useGetasPath();
  const { isDesktop } = useDevice();
  const token = useToken();

  const isLoggedIn = !!token;
  const { favoriteLists, refetchFavoriteLists } = useGetFavoriteLists({
    token: token,
  });

  const defaultList = useMemo(() => {
    return findDefaultList(favoriteLists);
  }, [favoriteLists]);

  const {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
  } = useGetFavoriteListItems({
    wishListId: defaultList?.id ?? '',
    size: 1000,
  });

  const link = encodeURIComponent(`${process.env.NEXT_PUBLIC_HOST}${asPath}`);

  return (
    <TourCardContainer>
      <Wrapper>
        <SpacingRow>
          <Row>
            <TitleBorder />
            <Title>
              <Typography variant='Title3' color='D400' weight='Black'>
                {name}
              </Typography>
            </Title>
          </Row>

          <IconContainer>
            <MyFavoriteComponent
              itemId={itemId}
              itemName={name}
              isTour={true}
              imageUrl={imageUrl ?? ''}
              type={FavoriteItemType.TOUR}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              defaultList={defaultList}
              defaultListItems={
                isFavoriteListItemError ? [] : favoriteListItems?.data
              }
              refetchDefaultList={refetchFavoriteListItems}
              fontColor='D400'
            />
            <SocialShare link={link} />
          </IconContainer>
        </SpacingRow>
        <DateDiv>
          <Date>
            <Typography variant='caption2' color='D400'>
              {date
                ? `${convertDateAsOnlyMonthString(date)}'den itibaren`
                : null}
            </Typography>
          </Date>
        </DateDiv>

        <FlexibleDiv bottom={isBadge && isDesktop ? 0 : 10}>
          <Row>
            {segments
              ? segments.map((item, index) =>
                  item.typeName === 'Segment' ? (
                    <DetailSegments key={index}>
                      <Segments key={index} segment={item} />
                    </DetailSegments>
                  ) : null
                )
              : null}
            {isBadge ? (
              <FlexibleDiv right={6}>
                <GuestSupportBanner />
              </FlexibleDiv>
            ) : null}
          </Row>
        </FlexibleDiv>

        {!isDesktop ? (
          <SummaryCardMobileDescription
            onNavigate={onNavigate}
            prices={prices}
            roomInformation={roomInformation}
            isExpedia={isExpedia}
            whereToGo={whereToGo}
            tourType={tourType}
            installments={installments}
            isEmpty={isEmpty}
            toggleLetUsCallYou={toggleLetUsCallYou}
            name={name}
            description={description}
          />
        ) : null}

        {isDesktop && (
          <Col>
            <Location isBold>Gezilecek Yerler</Location>
            <Location>
              <Typography variant='caption2' color='D400'>
                {whereToGo}
              </Typography>
            </Location>
          </Col>
        )}

        {installments?.length ? (
          <OpportunityCard
            showInstallments={toggleInstallments}
            showKocAilem={showKocAilem}
            items={formatTourPromotions(promotions)}
            isBankInstallments={Boolean(installments?.length > 0)}
            showMoreControl={
              promotions ? Boolean(promotions?.length > 0) : false
            }
          />
        ) : null}

        {promotions && haveLoyaltyPromotion(promotions) && !isLoggedIn ? (
          <>
            {isDesktop ? <Separator size='10px' /> : null}
            <LoyaltyLoginBanner />
          </>
        ) : null}

        {!isDesktop ? (
          isEmpty ? (
            <>
              <Separator size='15px' />
              <StyledButton variant='Primary' onClick={toggleLetUsCallYou}>
                Biz Sizi Arayalım
              </StyledButton>
            </>
          ) : (
            <>
              <Separator size='15px' />
              <StyledButton variant='Primary' onClick={onNavigate}>
                Tarihleri Gör
              </StyledButton>
            </>
          )
        ) : null}

        {description.length > 300 ? (
          <ReadMoreSection text='Devamını Oku' onClick={onReadMore} />
        ) : (
          <ReadMoreEmptySection />
        )}
      </Wrapper>
      {isDesktop ? (
        <SummaryCardDesktopDescription
          onNavigate={onNavigate}
          prices={prices}
          roomInformation={roomInformation}
          isExpedia={isExpedia}
          whereToGo={whereToGo}
          tourType={tourType}
          installments={installments}
          isEmpty={isEmpty}
          toggleLetUsCallYou={toggleLetUsCallYou}
          name={name}
          description={description}
        />
      ) : null}
    </TourCardContainer>
  );
};

export default SummaryCad;
